// "Evolução de ganhos mensais" on /earn/wallet. The backend
// (myWalletMonthlyEarnings) buckets "Total ganho" by São Paulo month: clips in
// the month they were sent, commissions and royalties in the month of the sale.
// Its rows come oldest first and the last one is the current, unfinished month.

// Hand-written: production introspection is off (see CLAUDE.md, codegen).
export type WalletEarningsMonth = {
  month: string;
  clips_cents: number;
  affiliate_cents: number;
  producer_cents: number;
  total_cents: number;
};

export type MyWalletMonthlyEarningsData = {
  myWalletMonthlyEarnings: { currency: string; months: WalletEarningsMonth[] };
};
export type MyWalletMonthlyEarningsVars = { months: number };

export const MONTHLY_EARNINGS_RANGES = [6, 12] as const;
export type MonthlyEarningsRange = (typeof MONTHLY_EARNINGS_RANGES)[number];
// One request covers every range; switching ranges never refetches.
export const MONTHLY_EARNINGS_FETCH_MONTHS = 12;

export type EarningsSourceKey = 'clips' | 'affiliate' | 'producer';

export type MonthlyEarningsBar = WalletEarningsMonth & {
  label: string;
  fullLabel: string;
  current: boolean;
  // Every source with earnings, only when the month has more than one, so a
  // clips-only month does not repeat its total.
  sources: { key: EarningsSourceKey; cents: number }[];
};

export type MonthlyEarningsChart = {
  bars: MonthlyEarningsBar[];
  totalCents: number;
  hasEarnings: boolean;
  bestMonth: MonthlyEarningsBar | null;
};

const monthDate = (month: string) => {
  const [year, index] = month.split('-').map(Number);
  return new Date(Date.UTC(year, index - 1, 15));
};

const shortMonth = (month: string, locale: string) => {
  const label = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
    .format(monthDate(month))
    .replace('.', '');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const longMonth = (month: string, locale: string) => new Intl.DateTimeFormat(locale, {
  month: 'long', year: 'numeric', timeZone: 'UTC',
}).format(monthDate(month));

export function buildMonthlyEarningsChart({
  months, range, locale,
}: {
  months: WalletEarningsMonth[];
  range: MonthlyEarningsRange;
  locale: string;
}): MonthlyEarningsChart {
  const visible = months.slice(-range);
  const bars = visible.map((row, index): MonthlyEarningsBar => {
    const sources = ([
      { key: 'clips', cents: row.clips_cents },
      { key: 'affiliate', cents: row.affiliate_cents },
      { key: 'producer', cents: row.producer_cents },
    ] as const).filter((source) => source.cents !== 0);
    return {
      ...row,
      label: shortMonth(row.month, locale),
      fullLabel: longMonth(row.month, locale),
      current: index === visible.length - 1,
      sources: sources.length > 1 ? sources.map((source) => ({ ...source })) : [],
    };
  });
  const totalCents = bars.reduce((sum, bar) => sum + bar.total_cents, 0);
  const bestMonth = bars.reduce<MonthlyEarningsBar | null>(
    (best, bar) => (bar.total_cents > (best?.total_cents ?? 0) ? bar : best),
    null,
  );
  return {
    bars,
    totalCents,
    hasEarnings: bars.some((bar) => bar.total_cents !== 0),
    bestMonth,
  };
}

// Y-axis ticks in cents, stepping by a round amount of reais (1, 2, 2.5 or 5
// times a power of ten, at least R$ 1) so every label is exact; recharts'
// own ticks are round in cents, which put R$ 550 / R$ 1.650 on the axis.
const AXIS_INTERVALS = 4;
const NICE_STEPS = [1, 2, 2.5, 5, 10];

export function earningsAxisTicks(maxCents: number): number[] {
  if (!(maxCents > 0)) return [0];
  const maxReais = maxCents / 100;
  const rough = maxReais / AXIS_INTERVALS;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const nice = NICE_STEPS.find((step) => step * magnitude >= rough) ?? 10;
  const stepCents = Math.max(100, Math.round(nice * magnitude * 100));
  const count = Math.ceil(maxCents / stepCents);
  return Array.from({ length: count + 1 }, (_, index) => index * stepCents);
}

export const formatCompactCents = (cents: number, currency: string): string => (
  new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100)
);
